'use client';

import { useState } from 'react';
import { calculateBudget } from './budget';
import { BudgetCalculatorForm } from './BudgetCalculatorForm';
import { BudgetCalculatorResult } from './BudgetCalculatorResult';
import {
  BUDGET_FIELDS,
  DISCLAIMER,
  INCOME_ERROR,
  INCOME_FIELD,
  INITIAL_VALUES,
  MAX_FIELD_VALUE,
  RANGE_ERROR,
} from './constants';
import { formatCurrency, parseCurrency, sanitizeCurrencyInput } from './currency';
import type { BudgetFieldErrors, BudgetFieldValues, BudgetState } from './types';

const INITIAL_STATE: BudgetState = { values: INITIAL_VALUES, errors: {}, result: null };

const validateValues = (values: BudgetFieldValues): BudgetFieldErrors => {
  const errors: BudgetFieldErrors = {};

  // Sanitization removes negative signs, leaving only an upper-bound check.
  BUDGET_FIELDS.forEach(({ label }) => {
    if (parseCurrency(values[label]) > MAX_FIELD_VALUE) errors[label] = RANGE_ERROR;
  });

  // Segments use whole cents; a sub-cent income would divide by zero.
  const incomeInCents = Math.round(parseCurrency(values[INCOME_FIELD.label]) * 100);

  if (incomeInCents <= 0) errors[INCOME_FIELD.label] = INCOME_ERROR;

  return errors;
};

export const BudgetCalculator = () => {
  const [state, setState] = useState<BudgetState>(INITIAL_STATE);

  const updateValue = (label: string, transform: (raw: string) => string) =>
    setState(previous => ({
      ...previous,
      values: { ...previous.values, [label]: transform(previous.values[label]) },
    }));

  const handleFieldChange = (label: string, value: string) => updateValue(label, () => sanitizeCurrencyInput(value));

  // Restore raw digits on focus after formatting on blur.
  const handleFieldFocus = (label: string) => updateValue(label, raw => (raw === '' ? '' : String(parseCurrency(raw))));

  const handleFieldBlur = (label: string) =>
    updateValue(label, raw => (raw === '' ? '' : formatCurrency(parseCurrency(raw))));

  const handleCalculate = () =>
    setState(previous => {
      const errors = validateValues(previous.values);
      const hasErrors = Object.keys(errors).length > 0;

      return {
        ...previous,
        errors,
        result: hasErrors ? null : calculateBudget(previous.values, parseCurrency(previous.values[INCOME_FIELD.label])),
      };
    });

  /** Reset and Start over clear the same state; only Start over is available from results. */
  const handleClear = () => setState(INITIAL_STATE);

  /** Preserve values so a single adjustment can be recalculated. */
  const handleEdit = () => setState(previous => ({ ...previous, result: null }));

  return (
    <div className="rounded-xl bg-bg-raised-secondary p-6 font-sans sm:p-10">
      {/* Keep this region mounted so screen readers announce panel changes. */}
      <div aria-live="polite">
        {state.result !== null && (
          <BudgetCalculatorResult result={state.result} onEdit={handleEdit} onStartOver={handleClear} />
        )}
      </div>

      {state.result === null && (
        <BudgetCalculatorForm
          values={state.values}
          errors={state.errors}
          onFieldChange={handleFieldChange}
          onFieldFocus={handleFieldFocus}
          onFieldBlur={handleFieldBlur}
          onCalculate={handleCalculate}
          onReset={handleClear}
        />
      )}

      <p className="mt-8 text-legal">{DISCLAIMER}</p>
    </div>
  );
};
